import { getTileUrl, getTileAttribution } from "../../utils/map-utils";
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Skeleton,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  LocalShipping,
  Speed,
  People,
  MedicalServices,
  Route,
  Build,
  AccessTime,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import KpiCard from '../../components/monitoramento/KpiCard';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import { ChatIAButton } from '../../components/ChatIA';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const STATUS_COLORS: Record<string, string> = {
  em_atendimento: '#22c55e',
  em_deslocamento: '#007AF0',
  parada: '#94a3b8',
  manutencao: '#E76F00',
};

const STATUS_LABELS: Record<string, string> = {
  em_atendimento: 'Em Atendimento',
  em_deslocamento: 'Em Deslocamento',
  parada: 'Parada',
  manutencao: 'Manutencao',
};

const PIE_COLORS = ['#22c55e', '#ef4444', '#f59e0b'];

// Icon cache to avoid recreating DivIcon instances every render/tick
const ICON_CACHE = new Map<string, L.DivIcon>();

function getCachedIcon(color: string, pulse: boolean): L.DivIcon {
  const key = `${color}-${pulse}`;
  let icon = ICON_CACHE.get(key);
  if (icon) return icon;
  const pulseStyle = pulse
    ? `animation: pulse-ring 1.5s cubic-bezier(0.215,0.61,0.355,1) infinite;`
    : '';
  icon = new L.DivIcon({
    className: '',
    html: `
      <div style="position:relative;">
        ${pulse ? `<div style="
          position:absolute; top:-8px; left:-8px;
          width:40px; height:40px;
          border-radius:50%;
          background:${color}33;
          ${pulseStyle}
        "></div>` : ''}
        <div style="
          width:24px; height:24px;
          background:${color};
          border:3px solid #fff;
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          box-shadow:0 2px 6px rgba(0,0,0,0.35);
          position:relative; z-index:2;
        "></div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
  ICON_CACHE.set(key, icon);
  return icon;
}

interface UnidadeMovel {
  codigo_unidade: number;
  nome_unidade: string;
  codcnes: string;
  latitude: number;
  longitude: number;
  cnpj: string;
  responsavel_tecnico: string;
  natureza_juridica: string;
  status_atual: string;
  servicos: string;
  instalacoes: string;
  veiculo_id: number;
  placa: string;
  veiculo_descricao: string;
  veiculo_modelo: string;
  tipo_veiculo: string;
  veiculo_ano: string;
  veiculo_situacao: string;
}

interface PontoFixo {
  codigo_unidade: number;
  nome_unidade: string;
  codcnes: string;
  nome_tipo_unidade: string;
  latitude: number;
  longitude: number;
}

interface RotaPonto {
  seq: number;
  latitude: number;
  longitude: number;
  timestamp_ponto: string;
  tipo_ponto: string;
  duracao_minutos: number;
}

interface SimPonto {
  latitude: number;
  longitude: number;
}

interface RotaSimulacao {
  viagem_id: number;
  fro_veiculo_id: number;
  pontos: SimPonto[] | null;
}

interface Rota {
  viagem_id: number;
  fro_veiculo_id: number;
  placa: string;
  veiculo_descricao: string;
  data_saida: string;
  hora_saida: string;
  nome_rota: string;
  distancia_rota: number;
  situacao: string;
  total_pontos: number;
  pontos?: RotaPonto[];
}

interface Eficiencia {
  codigo_unidade: number;
  nome_unidade: string;
  total_vagas: number;
  total_agendados: number;
  total_disponivel: number;
  total_realizados: number;
  capacidade_pct: number;
  absenteismo_pct: number;
  perda_pct: number;
}

interface Profissional {
  codigo_unidade: number;
  nome_unidade: string;
  nome: string;
  cbo: string;
  especialidade: string;
  cns: string;
  producao_mensal: number;
  vinculo: string;
}

interface DataResponse {
  unidades: UnidadeMovel[];
  pontosFixos: PontoFixo[];
  kpis: {
    total_vagas: number;
    total_agendados: number;
    total_disponivel: number;
    capacidade_pct: number;
    absenteismo_pct: number;
    perda_pct: number;
  };
  eficiencia: Eficiencia[];
  profissionais: Profissional[];
  rotas: Rota[];
  producao: any[];
  rotasSimulacao: RotaSimulacao[];
}

const MonitoramentoUnidadesMoveis: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<DataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedUnidade, setSelectedUnidade] = useState<number>(0);
  const [selectedRotaUnidade, setSelectedRotaUnidade] = useState<number>(0);
  const [selectedRota, setSelectedRota] = useState<number | null>(null);
  const [rotaPontos, setRotaPontos] = useState<RotaPonto[] | null>(null);
  const [loadingPontos, setLoadingPontos] = useState(false);
  const [simPositions, setSimPositions] = useState<Record<number, { lat: number; lng: number; speed: number }>>({});

  // Data range: 1 mes (otimizado, era 3 meses)
  const now = new Date();
  const defaultEnd = now.toISOString().split('T')[0];
  const defaultStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
  const [dataInicio, setDataInicio] = useState(defaultStart);
  const [dataFim, setDataFim] = useState(defaultEnd);

  const fetchData = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError('');
    try {
      const result = await monitoramentoService.getUnidadesMoveis(municipioAtivo.id, dataInicio, dataFim);
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar dados de unidades moveis');
    } finally {
      setLoading(false);
    }
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Lazy-load waypoints when a route is selected
  useEffect(() => {
    if (!selectedRota || !municipioAtivo?.id) {
      setRotaPontos(null);
      return;
    }
    let cancelled = false;
    setLoadingPontos(true);
    monitoramentoService.getRotaPontos(municipioAtivo.id, selectedRota)
      .then((pontos: RotaPonto[]) => {
        if (!cancelled) setRotaPontos(pontos.filter(p => p.latitude != null && p.longitude != null));
      })
      .catch(() => {
        if (!cancelled) setRotaPontos(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingPontos(false);
      });
    return () => { cancelled = true; };
  }, [selectedRota, municipioAtivo?.id]);

  // Simulacao de movimento — usa rotasSimulacao (dados leves, apenas unidades em deslocamento)
  useEffect(() => {
    if (!data?.unidades || !data?.rotasSimulacao) return;

    const movingUnits = data.unidades.filter(u => u.status_atual === 'em_deslocamento');
    if (movingUnits.length === 0) return;

    const unitRotas: Record<number, SimPonto[]> = {};
    movingUnits.forEach(u => {
      const sim = data.rotasSimulacao.find(r => r.fro_veiculo_id === u.veiculo_id);
      if (sim && sim.pontos && sim.pontos.length > 1) {
        unitRotas[u.codigo_unidade] = sim.pontos.filter(p => p.latitude && p.longitude);
      }
    });

    if (Object.keys(unitRotas).length === 0) return;

    const initPos: Record<number, { lat: number; lng: number; speed: number }> = {};
    const progressTracker: Record<number, number> = {};
    Object.entries(unitRotas).forEach(([codStr, pontos]) => {
      const cod = Number(codStr);
      if (pontos.length > 0) {
        initPos[cod] = { lat: pontos[0].latitude, lng: pontos[0].longitude, speed: 0 };
        progressTracker[cod] = 0;
      }
    });
    setSimPositions(initPos);

    const SPEED_KMH = 45;
    const TICK_SECONDS = 3;
    const DISTANCE_PER_TICK_KM = (SPEED_KMH * TICK_SECONDS) / 3600;
    const KM_PER_DEGREE = 111;

    const interval = setInterval(() => {
      setSimPositions(prev => {
        const next = { ...prev };
        Object.entries(unitRotas).forEach(([codStr, pontos]) => {
          const cod = Number(codStr);
          if (pontos.length < 2) return;

          let progress = progressTracker[cod] || 0;
          const fromIdx = Math.floor(progress) % pontos.length;
          const toIdx = (fromIdx + 1) % pontos.length;
          const from = pontos[fromIdx];
          const to = pontos[toIdx];

          const dlat = to.latitude - from.latitude;
          const dlng = to.longitude - from.longitude;
          const segmentDistKm = Math.sqrt(dlat * dlat + dlng * dlng) * KM_PER_DEGREE;

          const progressIncrement = segmentDistKm > 0.001
            ? DISTANCE_PER_TICK_KM / segmentDistKm
            : 1;

          const fraction = progress - Math.floor(progress);
          const newFraction = fraction + progressIncrement;

          if (newFraction >= 1) {
            progress = (Math.floor(progress) + 1) % pontos.length;
          } else {
            progress = Math.floor(progress) + newFraction;
          }
          progressTracker[cod] = progress;

          const curFromIdx = Math.floor(progress) % pontos.length;
          const curToIdx = (curFromIdx + 1) % pontos.length;
          const curFraction = progress - Math.floor(progress);
          const curFrom = pontos[curFromIdx];
          const curTo = pontos[curToIdx];

          const lat = curFrom.latitude + (curTo.latitude - curFrom.latitude) * curFraction;
          const lng = curFrom.longitude + (curTo.longitude - curFrom.longitude) * curFraction;

          const speedVariation = Math.round((Math.random() - 0.5) * 10);
          next[cod] = { lat, lng, speed: SPEED_KMH + speedVariation };
        });
        return next;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [data?.unidades, data?.rotasSimulacao]);

  const kpis = useMemo(() => {
    if (!data?.kpis) return null;
    const k = data.kpis;
    return {
      vagas: Number(k.total_vagas) || 0,
      agendados: Number(k.total_agendados) || 0,
      capacidade: Number(k.capacidade_pct) || 0,
      absenteismo: Number(k.absenteismo_pct) || 0,
      perda: Number(k.perda_pct) || 0,
    };
  }, [data?.kpis]);

  // ChatIA context — resumido, sem waypoints/rotas completas
  const chatContexto = useMemo(() => {
    if (!data) return '';
    return JSON.stringify({
      unidades: data.unidades.length,
      pontosFixos: data.pontosFixos.length,
      kpis: data.kpis,
      eficiencia: data.eficiencia.length,
      profissionais: data.profissionais.length,
      rotas: data.rotas.length,
      statusDistribuicao: data.unidades.reduce((acc, u) => {
        const s = u.status_atual || 'parada';
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    });
  }, [data]);

  const filteredRotas = useMemo(() => {
    if (!data?.rotas) return [];
    if (!selectedRotaUnidade) return data.rotas;
    const unidade = data.unidades.find(u => u.codigo_unidade === selectedRotaUnidade);
    if (!unidade) return data.rotas;
    return data.rotas.filter(r => r.fro_veiculo_id === unidade.veiculo_id);
  }, [data?.rotas, data?.unidades, selectedRotaUnidade]);

  const selectedRotaData = useMemo(() => {
    if (!selectedRota || !filteredRotas.length) return null;
    return filteredRotas.find(r => r.viagem_id === selectedRota) || null;
  }, [selectedRota, filteredRotas]);

  const filteredProfissionais = useMemo(() => {
    if (!data?.profissionais) return [];
    if (!selectedUnidade) return data.profissionais;
    return data.profissionais.filter(p => p.codigo_unidade === selectedUnidade);
  }, [data?.profissionais, selectedUnidade]);

  const getInstalacoes = useCallback((unidade: UnidadeMovel) => {
    try { return JSON.parse(unidade.instalacoes || '[]'); } catch { return []; }
  }, []);

  const getServicos = useCallback((unidade: UnidadeMovel) => {
    try { return JSON.parse(unidade.servicos || '[]'); } catch { return []; }
  }, []);

  if (!municipioAtivo) {
    return <Alert severity="warning" sx={{ m: 2 }}>Selecione um municipio para visualizar as unidades moveis.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Pulse CSS animation */}
      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(0.5); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            Unidades Moveis de Saude
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitoramento em tempo real e gestao de ambulancias SAMU
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DateRangeSelector
            dataInicio={dataInicio}
            dataFim={dataFim}
            onChange={(di, df) => { setDataInicio(di); setDataFim(df); }}
          />
          <ChatIAButton contexto="dashboard" dadosContexto={chatContexto} />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI Cards */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : kpis && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard titulo="Total Vagas" valor={kpis.vagas.toLocaleString('pt-BR')} icone={<MedicalServices />} cor="#007AF0" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard titulo="Agendados" valor={kpis.agendados.toLocaleString('pt-BR')} icone={<People />} cor="#22c55e" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard titulo="Capacidade" valor={`${kpis.capacidade}%`} icone={<Speed />} cor="#8b5cf6" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard titulo="Absenteismo" valor={`${kpis.absenteismo}%`} icone={<AccessTime />} cor="#f59e0b" />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <KpiCard titulo="Perda" valor={`${kpis.perda}%`} icone={<LocalShipping />} cor="#ef4444" />
          </Grid>
        </Grid>
      )}

      {/* Mapa */}
      {!loading && data?.unidades && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, position: 'relative' }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Mapa de Unidades Moveis — Tempo Real
          </Typography>
          <Box sx={{ height: 600, borderRadius: 2, overflow: 'hidden' }}>
            <MapContainer
              center={[-19.94, -44.06]}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution={getTileAttribution()}
                url={getTileUrl()}
              />
              {/* Markers de unidades moveis */}
              {data.unidades.map(u => {
                const simPos = simPositions[u.codigo_unidade];
                const lat = simPos?.lat || u.latitude;
                const lng = simPos?.lng || u.longitude;
                const status = u.status_atual || 'parada';
                const color = STATUS_COLORS[status] || '#94a3b8';
                const isPulse = status === 'em_deslocamento';
                return (
                  <Marker
                    key={`mobile-${u.codigo_unidade}`}
                    position={[lat, lng]}
                    icon={getCachedIcon(color, isPulse)}
                  >
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <strong>{u.nome_unidade}</strong><br />
                        <span style={{ fontSize: 12 }}>CNES: {u.codcnes}</span><br />
                        <Chip
                          label={STATUS_LABELS[status] || status}
                          size="small"
                          sx={{ mt: 0.5, mb: 0.5, bgcolor: color, color: '#fff', fontWeight: 600 }}
                        /><br />
                        <span style={{ fontSize: 12 }}>Veiculo: {u.placa} - {u.veiculo_modelo}</span><br />
                        {simPos && <span style={{ fontSize: 12 }}>Velocidade: ~{simPos.speed} km/h</span>}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
              {/* Pontos fixos UBS/UPA como CircleMarker (SVG, leve) */}
              {data.pontosFixos.map(pf => (
                <CircleMarker
                  key={`fixed-${pf.codigo_unidade}`}
                  center={[pf.latitude, pf.longitude]}
                  radius={6}
                  fillColor="#ef4444"
                  color="#fff"
                  weight={2}
                  fillOpacity={0.85}
                >
                  <Popup>
                    <div>
                      <strong>{pf.nome_unidade}</strong><br />
                      <span style={{ fontSize: 12 }}>CNES: {pf.codcnes}</span><br />
                      <span style={{ fontSize: 12 }}>{pf.nome_tipo_unidade}</span>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </Box>
          {/* Legenda */}
          <Paper sx={{
            position: 'absolute', top: 70, right: 24, p: 1.5, zIndex: 1000,
            bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, boxShadow: 2,
          }}>
            <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>Legenda</Typography>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: STATUS_COLORS[key] }} />
                <Typography variant="caption">{label}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
              <Typography variant="caption">UBS/UPA</Typography>
            </Box>
          </Paper>
          {/* Painel info */}
          <Paper sx={{
            position: 'absolute', bottom: 24, left: 24, p: 1.5, zIndex: 1000,
            bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, boxShadow: 2, maxWidth: 260,
          }}>
            <Typography variant="caption" fontWeight={700} display="block">
              {data.unidades.length} unidades moveis ativas
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {data.pontosFixos.length} pontos fixos (UBS/UPA)
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              Atualizacao a cada 3s (simulado)
            </Typography>
          </Paper>
        </Paper>
      )}

      {/* Tabs */}
      {!loading && data && (
        <>
          <Paper sx={{ mb: 2, borderRadius: 2 }}>
            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} variant="fullWidth">
              <Tab label="Identificacao" icon={<LocalShipping />} iconPosition="start" />
              <Tab label="Historico de Rotas" icon={<Route />} iconPosition="start" />
              <Tab label="Eficiencia Operacional" icon={<Speed />} iconPosition="start" />
              <Tab label="Recursos" icon={<Build />} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab 0 — Identificacao */}
          {tabIndex === 0 && (
            <Grid container spacing={2}>
              {data.unidades.map(u => {
                const status = u.status_atual || 'parada';
                return (
                  <Grid item xs={12} md={6} key={u.codigo_unidade}>
                    <Card sx={{ borderRadius: 2, borderLeft: `4px solid ${STATUS_COLORS[status] || '#94a3b8'}` }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6" fontWeight={600}>{u.nome_unidade}</Typography>
                          <Chip
                            label={STATUS_LABELS[status] || status}
                            size="small"
                            sx={{ bgcolor: STATUS_COLORS[status], color: '#fff', fontWeight: 600 }}
                          />
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">CNES</Typography>
                            <Typography variant="body2" fontWeight={600}>{u.codcnes}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">CNPJ</Typography>
                            <Typography variant="body2">{u.cnpj}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Responsavel Tecnico</Typography>
                            <Typography variant="body2">{u.responsavel_tecnico}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Natureza Juridica</Typography>
                            <Typography variant="body2">{u.natureza_juridica}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Coordenadas</Typography>
                            <Typography variant="body2">{u.latitude}, {u.longitude}</Typography>
                          </Grid>
                        </Grid>
                        {u.placa && (
                          <Box sx={{ mt: 1.5, p: 1, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">Veiculo</Typography>
                            <Typography variant="body2">
                              <strong>{u.placa}</strong> — {u.veiculo_modelo} ({u.veiculo_ano})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {u.tipo_veiculo} | Situacao: {u.veiculo_situacao}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Tab 1 — Historico de Rotas (lazy-load waypoints) */}
          {tabIndex === 1 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <InputLabel>Unidade</InputLabel>
                  <Select
                    value={selectedRotaUnidade}
                    label="Unidade"
                    onChange={e => { setSelectedRotaUnidade(Number(e.target.value)); setSelectedRota(null); }}
                  >
                    <MenuItem value={0}>Todas as unidades</MenuItem>
                    {data.unidades.map(u => (
                      <MenuItem key={u.codigo_unidade} value={u.codigo_unidade}>{u.nome_unidade}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 300 }}>
                  <InputLabel>Rota</InputLabel>
                  <Select
                    value={selectedRota || ''}
                    label="Rota"
                    onChange={e => setSelectedRota(Number(e.target.value))}
                  >
                    {filteredRotas.map(r => (
                      <MenuItem key={r.viagem_id} value={r.viagem_id}>
                        {r.data_saida} {r.hora_saida} — {r.nome_rota} ({r.total_pontos} pts)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Loading pontos indicator */}
              {loadingPontos && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Carregando waypoints...</Typography>
                </Box>
              )}

              {/* Mini-KPIs para rota selecionada */}
              {selectedRotaData && rotaPontos && !loadingPontos && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 1.5, textAlign: 'center', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Distancia</Typography>
                        <Typography variant="h6" fontWeight={700}>{selectedRotaData.distancia_rota} km</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 1.5, textAlign: 'center', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Paradas</Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {rotaPontos.filter((p: RotaPonto) => p.tipo_ponto?.includes('parada')).length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 1.5, textAlign: 'center', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Tempo Paradas</Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {rotaPontos.reduce((acc: number, p: RotaPonto) => acc + (p.duracao_minutos || 0), 0)} min
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 1.5, textAlign: 'center', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Veiculo</Typography>
                        <Typography variant="h6" fontWeight={700}>{selectedRotaData.placa}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Mini mapa da rota */}
                  {rotaPontos.length > 0 && rotaPontos[0]?.latitude != null && (
                    <Paper sx={{ p: 1, mb: 2, borderRadius: 2 }}>
                      <Box sx={{ height: 350, borderRadius: 1, overflow: 'hidden' }}>
                        <MapContainer
                          center={[
                            rotaPontos[0].latitude,
                            rotaPontos[0].longitude,
                          ]}
                          zoom={13}
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution={getTileAttribution()}
                            url={getTileUrl()}
                          />
                          <Polyline
                            positions={rotaPontos
                              .filter((p: RotaPonto) => p.latitude && p.longitude)
                              .map((p: RotaPonto) => [p.latitude, p.longitude] as [number, number])}
                            color="#007AF0"
                            weight={3}
                          />
                          {rotaPontos
                            .filter((p: RotaPonto) => p.tipo_ponto?.includes('parada'))
                            .map((p: RotaPonto, i: number) => (
                              <CircleMarker
                                key={i}
                                center={[p.latitude, p.longitude]}
                                radius={8}
                                fillColor="#ef4444"
                                color="#fff"
                                weight={2}
                                fillOpacity={0.9}
                              >
                                <Popup>
                                  <div>
                                    <strong>Parada de Atendimento</strong><br />
                                    Duracao: {p.duracao_minutos} min<br />
                                    {p.timestamp_ponto && new Date(p.timestamp_ponto).toLocaleString('pt-BR')}
                                  </div>
                                </Popup>
                              </CircleMarker>
                            ))}
                        </MapContainer>
                      </Box>
                    </Paper>
                  )}

                  {/* Timeline */}
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell>Hora</TableCell>
                          <TableCell>Coordenadas</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Duracao</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rotaPontos.map((p: RotaPonto, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{p.timestamp_ponto ? new Date(p.timestamp_ponto).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {p.latitude?.toFixed(5)}, {p.longitude?.toFixed(5)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={p.tipo_ponto === 'deslocamento' ? 'Deslocamento' : 'Parada Atendimento'}
                                size="small"
                                color={p.tipo_ponto === 'deslocamento' ? 'primary' : 'error'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{p.duracao_minutos > 0 ? `${p.duracao_minutos} min` : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {!selectedRota && (
                <Alert severity="info">Selecione uma rota para visualizar o historico de deslocamento.</Alert>
              )}
            </Box>
          )}

          {/* Tab 2 — Eficiencia Operacional */}
          {tabIndex === 2 && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* BarChart */}
                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 2, borderRadius: 2, height: 350 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Agendados vs Realizados por Unidade</Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data.eficiencia.map(e => ({
                        nome: e.nome_unidade?.replace('SAMU ', '').substring(0, 20),
                        Agendados: Number(e.total_agendados),
                        Realizados: Number(e.total_realizados),
                      }))} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 11 }} />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="Agendados" fill="#007AF0" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="Realizados" fill="#22c55e" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                {/* PieChart */}
                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 2, borderRadius: 2, height: 350 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Distribuicao Geral</Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Realizados', value: data.eficiencia.reduce((s, e) => s + Number(e.total_realizados), 0) },
                            { name: 'Absenteismo', value: data.eficiencia.reduce((s, e) => s + (Number(e.total_agendados) - Number(e.total_realizados)), 0) },
                            { name: 'Perda', value: data.eficiencia.reduce((s, e) => s + Number(e.total_disponivel), 0) },
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                        >
                          {PIE_COLORS.map((color, i) => (
                            <Cell key={i} fill={color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>

              {/* Tabela eficiencia */}
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell>Unidade</TableCell>
                      <TableCell align="right">Vagas</TableCell>
                      <TableCell align="right">Agendados</TableCell>
                      <TableCell align="right">Realizados</TableCell>
                      <TableCell align="right">Capacidade %</TableCell>
                      <TableCell align="right">Absenteismo %</TableCell>
                      <TableCell align="right">Perda %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.eficiencia.map(e => (
                      <TableRow key={e.codigo_unidade}>
                        <TableCell>{e.nome_unidade}</TableCell>
                        <TableCell align="right">{Number(e.total_vagas).toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">{Number(e.total_agendados).toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">{Number(e.total_realizados).toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${e.capacidade_pct}%`}
                            size="small"
                            sx={{
                              bgcolor: Number(e.capacidade_pct) >= 80 ? '#22c55e' : Number(e.capacidade_pct) >= 60 ? '#f59e0b' : '#ef4444',
                              color: '#fff', fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{e.absenteismo_pct}%</TableCell>
                        <TableCell align="right">{e.perda_pct}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 3 — Recursos */}
          {tabIndex === 3 && (
            <Box>
              <FormControl size="small" sx={{ minWidth: 300, mb: 2 }}>
                <InputLabel>Unidade</InputLabel>
                <Select
                  value={selectedUnidade}
                  label="Unidade"
                  onChange={e => setSelectedUnidade(Number(e.target.value))}
                >
                  <MenuItem value={0}>Todas as unidades</MenuItem>
                  {data.unidades.map(u => (
                    <MenuItem key={u.codigo_unidade} value={u.codigo_unidade}>{u.nome_unidade}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Instalacoes + Servicos */}
              {(selectedUnidade ? data.unidades.filter(u => u.codigo_unidade === selectedUnidade) : data.unidades).map(u => (
                <Paper key={u.codigo_unidade} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{u.nome_unidade}</Typography>

                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>Instalacoes / Equipamentos</Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {getInstalacoes(u).map((inst: any, i: number) => (
                      <Grid item xs={6} sm={4} md={3} key={i}>
                        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderRadius: 1 }}>
                          <Build sx={{ color: '#007AF0', fontSize: 20 }} />
                          <Typography variant="body2" fontWeight={600}>{inst.tipo}</Typography>
                          <Typography variant="caption" color="text.secondary">Qtd: {inst.qtd}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>Servicos</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {getServicos(u).map((s: string, i: number) => (
                      <Chip key={i} label={s} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Paper>
              ))}

              {/* Profissionais */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, mt: 2 }}>Equipe Profissional</Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell>Nome</TableCell>
                      <TableCell>CBO</TableCell>
                      <TableCell>Especialidade</TableCell>
                      <TableCell>CNS</TableCell>
                      <TableCell>Unidade</TableCell>
                      <TableCell align="right">Producao Mensal</TableCell>
                      <TableCell>Vinculo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProfissionais.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{p.nome}</TableCell>
                        <TableCell>{p.cbo}</TableCell>
                        <TableCell>{p.especialidade}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.cns}</TableCell>
                        <TableCell>{p.nome_unidade}</TableCell>
                        <TableCell align="right">{p.producao_mensal}</TableCell>
                        <TableCell>
                          <Chip label={p.vinculo} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MonitoramentoUnidadesMoveis;
